import {
    Component,
    ComponentRef,
    Compiler,
    EventEmitter,
    Injector,
    NgModule,
    NgModuleRef,
    OnInit,
    Pipe,
    PipeTransform,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule, ActivatedRoute } from "@angular/router";
import { FlashMessagesService } from "angular2-flash-messages";
import * as mustache from "mustache";
import { cloneDeep } from "lodash";
import * as moment from 'moment';
import { Subscription } from "rxjs";
import 'moment-timezone';
import { CclMessageService, CclSharedModule } from "certain-common-library";
import { SharedService } from "../../../../modules/shared/services/shared.service";
import { CatalogService } from "../../../../modules/shared/services/catalog.service";
import { AttendeeSessionService } from "../../../../modules/shared/services/attendee-session.service";

@Component({
    selector: "instance-section",
    templateUrl: "./instance-section.view.html",
    providers: []
})
export class InstanceSectionView implements OnInit {
    private subscription: Subscription;
    private mustache = mustache;

    // Configurations
    additionalSettingConfig: any;
    generalSessionConfig: any;
    mySessionConfig: any;
    generalDataConfig: any;

    // Fields
    filter: any = {};
    sessionName: string = '';
    sessionInstances: any = [];
    output = '';
    localTimezoneKey = moment.tz.guess(true);
    defaultTimeZoneKey: any;

    // Properties for Actions
    isEnrolled = [];
    registeredCount = [];
    isWaitListed = [];
    canBeWaitListed = [];
    isInCart = [];
    isFavorite = [];
    favInstanceIds = [];

    // Container to attach each Session Instance Component
    @ViewChild("listViewContainer", { read: ViewContainerRef })
    listViewContainer: ViewContainerRef;
    private listComp: ComponentRef<any>;

    constructor(
        public sharedService: SharedService,
        private catalogService: CatalogService,
        private attendeeSessionService: AttendeeSessionService,
        private messageService: CclMessageService,
        private _flashMessagesService: FlashMessagesService,
        private compiler: Compiler,
        private injector: Injector,
        private moduleRef: NgModuleRef<any>,
        private route: ActivatedRoute
    ) {
        this.defaultTimeZoneKey = this.sharedService.event.timeZoneKey || 
            this.sharedService.timezone ||
            this.localTimezoneKey ||
            this.sharedService.timezonekey;
        this.subscription = this.messageService.getMessage().subscribe(message => this.listenMessage(message));
    }

    ngOnInit() {
        this.mySessionConfig = JSON.parse(this.sharedService.configs[this.sharedService.configTypes.MYSESSION].config);
        this.generalSessionConfig = JSON.parse(this.sharedService.configs[this.sharedService.configTypes.SESSION.general].config);
        this.generalDataConfig = JSON.parse(this.sharedService.configs[this.sharedService.configTypes.DATA.general].config);

        if(!('removeScheduleButtonLabel' in this.generalSessionConfig.catalogConfig.buttonConfig)) {
            this.generalSessionConfig.catalogConfig.buttonConfig.removeScheduleButtonLabel = 'Cancel';
        }

        if(!('instanceCheckValidation' in this.generalSessionConfig.catalogConfig)) {
            this.generalSessionConfig.catalogConfig['instanceCheckValidation'] = false;
        }

        if(!('inventoryLock' in this.generalSessionConfig.catalogConfig)) {
            this.generalSessionConfig.catalogConfig['inventoryLock'] = false;
        }
    }

    closeSessionInstances() {
        this.messageService.sendMessage("toggle-instances");
    }

    async listenMessage(message) {
        if (message.text === "update-session-instances") {
            if (message.data) {
                this.loadData(message.data);
            } else {
                this.listViewContainer.clear();
                this.sessionName = '';
                this.sessionInstances = [];
                this.filter = {};
                this.isEnrolled = [];
                this.registeredCount = [];
                this.isWaitListed = [];
                this.canBeWaitListed = [];
                this.isInCart = [];
                this.isFavorite = [];
                this.favInstanceIds = [];
            }
        } else if (message.text === "apply-my-timezone") {
            this.defaultTimeZoneKey = this.sharedService.timezone || this.defaultTimeZoneKey;
        }
    }

    async loadData(sessionId) {
        this.sharedService.showLoader = true;
        const isLoggedIn = !!this.sharedService.loggedInAttendee;

        // Load Favorite Session Instance Ids
        let favorites = null;
        if (isLoggedIn) {
            favorites = this.attendeeSessionService.getFavoritesData(this.sharedService.loggedInAttendee.uuid).toPromise();
        }

        // Load Session Instances By ID
        const sessionInstancesResponse = this.loadSessionInstances(sessionId);

        // Load Additional Settings or Default Additional Settings
        let jsonPath =
            `/accounts/${this.sharedService.event.fileDir}session_catalog_${this.sharedService.catalogUUID}.json` +
            "?random=" + new Date().getTime();
        const additionalSettings = this.catalogService.getJSONService(jsonPath).toPromise();

        this.favInstanceIds = [];
        if (isLoggedIn) {
            await favorites.then(response => response.body["sessions"].forEach((instance) => {
                if (!instance.isExcluded) {
                    this.favInstanceIds.push(instance.id);
                }
            }));
        }

        this.sessionInstances = [];
        await sessionInstancesResponse.then(response => {
            if (response.body != null && response.body["sessions"].length > 0) {
                this.sessionInstances = response.body["sessions"];
                this.sessionInstances.forEach((instance, index) => {
                    let viewOnly = instance.viewOnlyAttendeeTypes ? instance.viewOnlyAttendeeTypes.split(',') : [];
                    if(viewOnly.findIndex(element => element == this.sharedService.loggedInAttendee.type.id) > -1) {
                        instance.isViewOnly = true;
                    } else {
                        instance.isViewOnly = false;
                    }
                    instance.showAddToCart = false;
                    instance.showAddToSchedule = false;
                    instance.showWaitlistedButton = false;
                    this.isEnrolled[index] = instance.isEnrolled;
                    this.registeredCount[index] = instance.registered;
                    this.isWaitListed[index] = instance.isWaitListed;
                    this.canBeWaitListed[index] = instance.waitlistedLimit == null || instance.waitlistedLimit > instance.waitlisted || instance.isWaitListed;
                    this.isInCart[index] = instance.isAddedToCart && this.generalSessionConfig.catalogConfig.buttonConfig.enableAddToCartButton;
                    this.isFavorite[index] = !!this.favInstanceIds.find(id => id == instance.id);
                    if(instance.capacity == null || instance.capacity > instance.registered) {
                        instance.showAddToCart = true;
                        instance.showAddToSchedule = true;
                    } else if(instance.capacity <= instance.registered && (instance.waitlistedLimit == null || instance.waitlistedLimit > instance.waitlisted)) {
                        instance.showWaitlistedButton = true;
                    }
                });
                this.sessionName = this.sessionInstances[0].sessionTitle;
            }
        });

        await additionalSettings.then(async response => {
            this.additionalSettingConfig = response.body;
            if (!this.additionalSettingConfig) {
                jsonPath = './assets/i18n/en.json'
                const defaultAdditionalSettings = await this.catalogService.getJSONService(jsonPath).toPromise();
                this.additionalSettingConfig = defaultAdditionalSettings?.body;
            }
        }).catch(async err => {
            jsonPath = './assets/i18n/en.json'
            const defaultAdditionalSettings = await this.catalogService.getJSONService(jsonPath).toPromise();
            this.additionalSettingConfig = defaultAdditionalSettings?.body;
        });

        // Build View
        const param = this.buildParam();
        this.renderView(param);
    }

    async loadSessionInstances(sessionId) {
        if (this.sharedService.loggedInAttendee) {
            this.filter['registrationUUID'] = this.sharedService.loggedInAttendee.uuid;
        }
        this.filter['isPublished'] = true;
        this.filter['eventId'] = this.sharedService.event.id;
        this.filter['catalogUUID'] = this.sharedService.catalogUUID;
        this.filter['sessionIds'] = [sessionId];
        return this.attendeeSessionService.getSessions(this.filter).toPromise();
    }

    renderView(param) {
        if (this.sessionInstances && this.sessionInstances.length > 0 && this.listViewContainer) {
            this.listViewContainer.clear();
            this.output = this.createTemplate();
            this.createComponentFromRaw(this.output, this.listViewContainer, this.listComp, param);
        }
    }

    createTemplate() {
        let idx = 0;
        let list = {
            instances: this.sessionInstances,
            idx: () => {
                idx++;
                return;
            },
            index: () => {
                return idx;
            }
        };

        return this.mustache.render(
            "{@ddf.#instances@}<div class='session-instance-template'>" +
            this.getTemplate() +
            this.getActions() +
            this.getLabels() +
            "</div>{@ddf.idx@}{@ddf./instances@}",
            list
        );
    }

    getTemplate() {
        return "<div><strong>{@ddf.locationName@}</strong></div>" +
            "<div>{{'{@ddf.startTime@}'|toDateObj: '" + this.defaultTimeZoneKey + "'| date: '" + this.generalDataConfig.dateTimeFormats.dateFormat +
            "," + this.generalDataConfig.dateTimeFormats.timeFormat + "'}}" +
            " - {{'{@ddf.endTime@}'|toDateObj: '" + this.defaultTimeZoneKey + "'| date: '" + this.generalDataConfig.dateTimeFormats.timeFormat + "'}}</div>";
    }

    getActions() {
        if (this.sharedService.loggedInAttendee) {
            return "<div class='instance-actions-container'>" +
                this.actionAddToCart() +
                this.actionAddToSchedule() +
                this.actionAddToWaitList() +
                this.actionRemove() +
               "</div>";
        } else {
            return "<div class='instance-actions-container'></div>";
        }
    }

    getLabels() {
        if (this.sharedService.loggedInAttendee) {
            return "<div class='instance-labels-container'>" +
                this.labelInCart() +
                this.labelScheduled() +
                this.labelWaitListed() +
                this.iconAddToFavorites() +
               "</div>";
        } else {
            return "<div class='instance-labels-container'></div>";
        }
    }

    actionAddToCart() {
        return "<span *ngIf=\"!isEnrolled[{@ddf.index@}] && !isWaitListed[{@ddf.index@}] && !{@ddf.isViewOnly@} &&" +
            "({@ddf.showAddToCart@} || canBeWaitListed[{@ddf.index@}]) && !isInCart[{@ddf.index@}] && " +
            this.generalSessionConfig.catalogConfig.buttonConfig.enableAddToCartButton + "\" >" +
            "<div class='cursor-pointer general-session-catalog-add-schedule-btn session-instance-action' " +
            "(cclClick)='addToCart({@ddf.index@},{@ddf.id@})' >" +
            "<span class='label-align'>" + this.generalSessionConfig.catalogConfig.buttonConfig.cartButtonLabel +
            "</span></div></span>";
    }

    actionAddToSchedule() {
        return "<span *ngIf=\"!isEnrolled[{@ddf.index@}] && !isWaitListed[{@ddf.index@}] && !{@ddf.isViewOnly@} &&" +
            "{@ddf.showAddToSchedule@} && !" +
            this.generalSessionConfig.catalogConfig.buttonConfig.enableAddToCartButton + "\" >" +
            "<div class='cursor-pointer session-instance-action general-session-catalog-add-schedule-btn' " +
            "(cclClick)='addToSchedule({@ddf.index@},{@ddf.id@})' >" +
            "<span class='label-align'>" + this.additionalSettingConfig.cart_section.add_to_schedule +
            "</span></div></span>";
    }

    actionAddToWaitList() {
        return "<span *ngIf=\"!isEnrolled[{@ddf.index@}] && !isWaitListed[{@ddf.index@}] && !{@ddf.isViewOnly@} &&" +
           "{@ddf.showWaitlistedButton@} && canBeWaitListed[{@ddf.index@}] && !" +
           this.generalSessionConfig.catalogConfig.buttonConfig.enableAddToCartButton + "\" >" +
           "<div class='cursor-pointer session-instance-action general-session-catalog-add-schedule-btn' " +
           "(cclClick)='addToWaitList({@ddf.index@},{@ddf.id@})' >" +
           "<span class='label-align'>" + "Add to Waitlist" +
           "</span></div></span>";
    }

    actionRemove() {
        return "<span *ngIf=\"(isEnrolled[{@ddf.index@}] || isWaitListed[{@ddf.index@}] || isInCart[{@ddf.index@}]) && !{@ddf.isViewOnly@}\" >" +
            "<div class='cursor-pointer session-instance-action general-session-catalog-remove-schedule-btn' " +
            "(cclClick)='removeFrom({@ddf.index@},{@ddf.id@})' >" +
            "<span class='label-align'>" + this.generalSessionConfig.catalogConfig.buttonConfig.removeScheduleButtonLabel +
            "</span></div></span>";
    }

    labelInCart() {
        return "<span *ngIf=\"isInCart[{@ddf.index@}]\">" +
            "<div class='session-instance-label general-session-catalog-button-registered general-session-catalog-header-region-background-1-4' >" +
            "<span class='label-align'>" + "In Cart" +
            "</span></div></span>";
    }

    labelScheduled() {
        return "<span *ngIf=\"isEnrolled[{@ddf.index@}]\">" +
            "<div class='session-instance-label general-session-catalog-button-registered general-session-catalog-header-region-background-1-4' >" +
            "<span class='label-align'>" + this.generalSessionConfig.catalogConfig.buttonConfig.regLabel +
            "</span></div></span>";
    }

    labelWaitListed() {
        return "<span *ngIf=\"isWaitListed[{@ddf.index@}]\">" +
            "<div class='session-instance-label general-session-catalog-button-wait'>" +
            "<span class='label-align'>" + this.generalSessionConfig.catalogConfig.buttonConfig.waitLabel +
            "</span></div></span>";
    }

    iconAddToFavorites() {
        return "<i attr.title=\"{{isFavorite[{@ddf.index@}] ? 'Remove from Favorites' : 'Add to Favorites'}}\" " +
            "*ngIf=\"!isInCart[{@ddf.index@}] && !isEnrolled[{@ddf.index@}] && !isWaitListed[{@ddf.index@}] && !{@ddf.isViewOnly@}\" " +
            "class='fa instance-action-icon cursor-pointer' " +
            "[ngClass]=\"isFavorite[{@ddf.index@}] ? '" +
            this.generalSessionConfig.catalogConfig.buttonConfig.favIcon + "' : '" +
            this.generalSessionConfig.catalogConfig.buttonConfig.favIconSec + "'\" " +
            "(cclClick)='processFavorite({@ddf.index@},{@ddf.id@})'" +
            "></i>";
    }

    buildParam() {
        return {
            'attendeeSessionService': this.attendeeSessionService,
            'generalSessionConfig': this.generalSessionConfig,
            'sharedService': this.sharedService,
            'messageService': this.messageService,
            'isEnrolled': this.isEnrolled,
            'registeredCount': this.registeredCount,
            'isWaitListed': this.isWaitListed,
            'canBeWaitListed': this.canBeWaitListed,
            'isInCart': this.isInCart,
            'isFavorite': this.isFavorite
        };
    }

    private createComponentFromRaw(template: string, containerRef: any, componentRef: any, param: any) {
        
        template = template.replace(/{/g, '&#123;');
        template = template.replace(/}/g, '&#125;');

        template = template
            .replace(new RegExp('contenteditable="true"', "g"), "")
            .replace(new RegExp("contenteditable='true'", "g"), "");

        @CustomComponent({ template, selector: 'scTmpCmp' })
        class tmpCmp {
            isEnrolled = param['isEnrolled'];
            registeredCount = param['registeredCount'];
            isWaitListed = param['isWaitListed'];
            canBeWaitListed = param['canBeWaitListed'];
            isInCart = param['isInCart'];
            isFavorite = param['isFavorite'];
            attendeeSessionService = param['attendeeSessionService'];
            sharedService = param['sharedService'];
            messageService = param['messageService'];
            generalSessionConfig = param['generalSessionConfig'];

            ngOnInit() {}

            addToCart(index, instanceId) {
                this.sharedService.showLoader = true;
                const registrationUUID = this.sharedService.loggedInAttendee.uuid;

                if (this.isFavorite[index]) {
                    this.attendeeSessionService.processFav(registrationUUID, instanceId).subscribe(response => {
                        if (response["body"].hasOwnProperty("status") && response["body"].status == "200") {
                            this.messageService.sendMessage("move-fav-to-cart", instanceId);
                            this.isEnrolled[index] = false;
                            this.isWaitListed[index] = false;
                            this.isInCart[index] = true;
                            this.isFavorite[index] = false;
                        }
                    });
                } else {
                    const cartObject = {
                        registrationUUID: registrationUUID,
                        confSessionInstanceId: instanceId,
                        catalogUUID: this.sharedService.setup.uuid
                    };
                    this.attendeeSessionService.addToCart(cartObject, this.generalSessionConfig?.catalogConfig?.instanceCheckValidation).subscribe(response => {
                        if (response["body"].hasOwnProperty("registrationConfSessionInstanceCartDTO")) {
                            this.messageService.sendMessage("single-add-cart", instanceId);
                            this.isEnrolled[index] = false;
                            this.isWaitListed[index] = false;
                            this.isInCart[index] = true;
                            this.isFavorite[index] = false;
                        }
                    });
                }
            }

            addToSchedule(index, instanceId) {
                this.sharedService.showLoader = true;
                const registrationUUID = this.sharedService.loggedInAttendee.uuid;

                if (this.isFavorite[index]) {
                    this.attendeeSessionService.processFav(registrationUUID, instanceId).subscribe(response => {
                        if (response["body"].hasOwnProperty("status") && response["body"].status == "200") {
                            this.processCart(index, registrationUUID);
                        }
                    });
                } else {
                    const cartObject = {
                        registrationUUID: registrationUUID,
                        confSessionInstanceId: instanceId,
                        catalogUUID: this.sharedService.setup.uuid
                    };
                    this.attendeeSessionService.addToCart(cartObject, this.generalSessionConfig?.catalogConfig?.instanceCheckValidation).subscribe(response => {
                        if (response["body"].hasOwnProperty("registrationConfSessionInstanceCartDTO")) {
                            this.processCart(index, registrationUUID);
                        }
                    });
                }

            }

            private processCart(index, registrationUUID) {
                this.attendeeSessionService.processCart(registrationUUID, this.generalSessionConfig?.catalogConfig?.instanceCheckValidation).subscribe(response => {
                    this.messageService.sendMessage("process-cart", response.body);
                    this.isEnrolled[index] = response.body.registered.length > 0;
                    this.isWaitListed[index] = response.body.waitlisted.length > 0;
                    this.isInCart[index] = false;
                    this.isFavorite[index] = false;
                });
            }

            addToWaitList(index, instanceId) {
                this.addToSchedule(index, instanceId);
            }

            removeFrom(index, instanceId) {
                this.sharedService.showLoader = true;
                const registrationUUID = this.sharedService.loggedInAttendee.uuid;
                if (this.isEnrolled[index] || this.isWaitListed[index]) {
                    // Remove From Schedule or WaitList
                    this.attendeeSessionService.removeFromSchedule(instanceId, registrationUUID).subscribe(response => {
                        this.messageService.sendMessage("single-remove-schedule", instanceId);
                        this.isEnrolled[index] = false;
                        this.isWaitListed[index] = false;
                        this.isInCart[index] = false;
                        this.isFavorite[index] = false;
                    });
                } else if (this.isInCart[index]) {
                    // Remove From Cart
                    this.attendeeSessionService.removeFromCart(instanceId, registrationUUID).subscribe(response => {
                        this.messageService.sendMessage("single-remove-cart", instanceId);
                        this.isEnrolled[index] = false;
                        this.isWaitListed[index] = false;
                        this.isInCart[index] = false;
                        this.isFavorite[index] = false;
                    });
                }
            }

            processFavorite(index, instanceId) {
                const registrationUUID = this.sharedService.loggedInAttendee.uuid;
                this.sharedService.showLoader = true;
                if (this.isFavorite[index]) {
                    // Remove from Favorites
                    this.attendeeSessionService.removeFromFavourite(registrationUUID, instanceId).subscribe(response => {
                        this.messageService.sendMessage("single-remove-fav", instanceId);
                        this.isFavorite[index] = false;
                    });
                } else {
                    // Add to Favorites
                    const favObject = {
                        registrationUUID: registrationUUID,
                        confSessionInstanceId: instanceId,
                        catalogUUID: this.sharedService.setup.uuid
                    };
                    this.attendeeSessionService.addToFavourite(favObject).subscribe(response => {
                        this.messageService.sendMessage("single-add-fav", instanceId);
                        this.isFavorite[index] = true;
                    });
                }
            }
        }

        @Custompipe({
            name: "toDateObj"
        })
        class ToDateObj implements PipeTransform {
            transform(value: any, timezoneKey: any): any {
                if (value) {
                    return moment(value).tz(timezoneKey).format('YYYY-MM-DD HH:mm:00');
                } else {
                    return null;
                }
            }
        }

        @CustomNgModule({
            imports: [RouterModule, CommonModule, CclSharedModule],
            declarations: [tmpCmp, ToDateObj]
        })
        class tmpModule {}

        this.compiler.compileModuleAndAllComponentsAsync(tmpModule).then((factories) => {
            containerRef.clear();
            const f = factories.componentFactories.find(element => element.selector === 'scTmpCmp');
            componentRef = f.create(this.injector, [], null, this.moduleRef);
            componentRef.instance.name = "instance-list-component";
            containerRef.insert(componentRef.hostView);
        });
    }
}

function CustomComponent(annotation: any) {
    return function(target: Function) {
        const component = new Component(annotation);
        Component(component)(target);
    };
}

function CustomNgModule(annotation: any) {
    return function(target: Function) {
        const ngModule = new NgModule(annotation);
        NgModule(ngModule)(target);
    };
}

function Custompipe(annotation: any) {
    return function(target: Function) {
        const pipe = new Pipe(annotation);
        Pipe(pipe)(target);
    };
}